import Decimal from 'decimal.js';

/**
 * P21 PR 11b – KPI để tăng lương (phương án Nhân sự §8.4, DEC-314). Mang phần KPI của app Minhtien-CRM sang:
 * kỳ đánh giá → phiếu mỗi NV theo bộ tiêu chí phòng mình → NV tự chấm → Trưởng phòng chấm → khóa kỳ (điểm tổng hợp, xếp loại)
 * → đề xuất tăng lương theo % của xếp loại → Giám đốc duyệt → hồ sơ lương mới từ tháng sau + phụ lục HĐ ký trên ứng dụng.
 */
export const KPI_CYCLE_STATUSES = ['draft', 'open', 'locked'] as const;
export type KpiCycleStatus = (typeof KPI_CYCLE_STATUSES)[number];
export const KPI_CYCLE_STATUS_LABELS: Record<KpiCycleStatus, string> = { draft: 'Nháp', open: 'Đang chấm', locked: 'Đã khóa' };

export const KPI_PERIOD_KINDS = ['month', 'quarter', 'half', 'year'] as const;
export type KpiPeriodKind = (typeof KPI_PERIOD_KINDS)[number];
export const KPI_PERIOD_KIND_LABELS: Record<KpiPeriodKind, string> = { month: 'Tháng', quarter: 'Quý', half: '6 tháng', year: 'Năm' };

export const KPI_REVIEW_STATUSES = ['draft', 'self_done', 'reviewed', 'final'] as const;
export type KpiReviewStatus = (typeof KPI_REVIEW_STATUSES)[number];
export const KPI_REVIEW_STATUS_LABELS: Record<KpiReviewStatus, string> = {
  draft: 'Chờ NV tự chấm',
  self_done: 'Chờ TP chấm',
  reviewed: 'TP đã chấm',
  final: 'Đã chốt',
};

export const KPI_AUTO_SOURCES = ['attendance', 'violations', 'training', 'sales'] as const;
export type KpiAutoSource = (typeof KPI_AUTO_SOURCES)[number];
export const KPI_AUTO_SOURCE_LABELS: Record<KpiAutoSource, string> = {
  attendance: 'Chuyên cần (chấm công)',
  violations: 'Vi phạm chấm công',
  training: 'Đào tạo (khóa đã đạt)',
  sales: 'Doanh số đơn bán',
};

export const KPI_GRADES = ['A', 'B', 'C', 'D', 'E'] as const;
export type KpiGrade = (typeof KPI_GRADES)[number];
/** Thang xếp loại theo CRM: ≥ 90 A, ≥ 80 B, ≥ 70 C, ≥ 50 D, dưới 50 E */
export const KPI_GRADE_MIN: Record<KpiGrade, number> = { A: 90, B: 80, C: 70, D: 50, E: 0 };
export const KPI_GRADE_LABELS: Record<KpiGrade, string> = { A: 'A – Xuất sắc', B: 'B – Tốt', C: 'C – Đạt', D: 'D – Trung bình', E: 'E – Không đạt' };
/** % tăng lương gợi ý mặc định (bảng kpi_raise_rules chỉnh được) */
export const KPI_RAISE_DEFAULTS: Record<KpiGrade, number> = { A: 10, B: 7, C: 4, D: 0, E: 0 };

export const SALARY_RAISE_STATUSES = ['proposed', 'approved', 'rejected'] as const;
export type SalaryRaiseStatus = (typeof SALARY_RAISE_STATUSES)[number];
export const SALARY_RAISE_STATUS_LABELS: Record<SalaryRaiseStatus, string> = { proposed: 'Chờ GĐ duyệt', approved: 'Đã duyệt', rejected: 'Không tăng' };

export function kpiGrade(score: number | null | undefined): KpiGrade | null {
  if (score === null || score === undefined || !Number.isFinite(score)) return null;
  return KPI_GRADES.find((g) => score >= KPI_GRADE_MIN[g]) ?? 'E';
}

export interface KpiScoreLine {
  weight: number;
  selfScore: number | null;
  managerScore: number | null;
}

/**
 * Điểm tổng hợp = Σ(điểm × trọng số) / Σ trọng số, 2 số lẻ (ROUND_HALF_UP). Tổng trọng số ≠ 100 vẫn ra thang 100.
 * which = 'self': chỉ điểm tự chấm; 'manager': điểm TP, dòng TP chưa chấm lấy điểm tự chấm; dòng không có điểm tính 0.
 * Tổng trọng số 0 → null.
 */
export function kpiWeightedScore(lines: KpiScoreLine[], which: 'self' | 'manager'): number | null {
  let w = new Decimal(0);
  let s = new Decimal(0);
  for (const l of lines) {
    const weight = new Decimal(l.weight || 0);
    const score = which === 'self' ? l.selfScore : (l.managerScore ?? l.selfScore);
    w = w.plus(weight);
    s = s.plus(weight.times(score ?? 0));
  }
  if (w.isZero()) return null;
  return s.div(w).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
}

/** Lương đề xuất = lương hiện tại × (1 + %), làm tròn đồng (ROUND_HALF_UP) */
export function raisedSalary(current: number, percent: number): number {
  return new Decimal(current).times(new Decimal(100).plus(percent)).div(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
}

/** Ngày 1 của tháng sau ngày `date` (YYYY-MM-DD) */
export function firstOfNextMonth(date: string): string {
  const [y, m] = date.split('-').map(Number) as [number, number];
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return `${ny}-${String(nm).padStart(2, '0')}-01`;
}

/**
 * Bộ tiêu chí mẫu theo phòng ban (từ CRM, nguồn infographic "Bộ tiêu chí KPI cho nhân viên"). Giữ nguyên trọng số
 * như CRM (mỗi bộ cộng 105%; điểm tổng hợp chia theo tổng trọng số nên vẫn ra thang 100). "Đi làm đúng giờ" lấy số chuyên cần.
 */
export interface KpiTemplate {
  key: string;
  name: string;
  goal: string;
  criteria: { name: string; weight: number; target?: string; autoSource?: KpiAutoSource }[];
}
const ON_TIME = { name: 'Đi làm đúng giờ', weight: 5, target: '≥ 95%', autoSource: 'attendance' as const };
export const KPI_TEMPLATES: KpiTemplate[] = [
  {
    key: 'KINH_DOANH', name: 'Phòng Kinh doanh / Bán hàng', goal: 'Doanh số vượt kế hoạch - Khách hàng hài lòng - Công nợ an toàn',
    criteria: [
      { name: 'Doanh số', weight: 30, autoSource: 'sales' }, { name: 'Lợi nhuận / Biên lợi nhuận', weight: 15 }, { name: 'Khách hàng mới', weight: 10 },
      { name: 'Tỷ lệ chốt đơn', weight: 10 }, { name: 'Thu hồi công nợ', weight: 10 }, { name: 'Chăm sóc khách hàng', weight: 5 },
      { name: 'Báo cáo & CRM', weight: 5 }, { name: 'Tuân thủ quy trình', weight: 5, autoSource: 'violations' }, ON_TIME, { name: 'Thái độ & phối hợp', weight: 10 },
    ],
  },
  {
    key: 'KE_TOAN', name: 'Phòng Kế toán', goal: 'Số liệu chính xác - Báo cáo đúng hạn - Tuân thủ quy định',
    criteria: [
      { name: 'Hoàn thành công việc đúng hạn', weight: 20 }, { name: 'Độ chính xác số liệu', weight: 20 }, { name: 'Báo cáo tài chính/kế toán', weight: 15 },
      { name: 'Công nợ phải thu', weight: 10 }, { name: 'Công nợ phải trả', weight: 5 }, { name: 'Thuế & hồ sơ pháp lý', weight: 10 },
      { name: 'Kiểm soát chứng từ', weight: 10 }, { name: 'Phối hợp nội bộ', weight: 5 }, ON_TIME, { name: 'Tuân thủ bảo mật', weight: 5 },
    ],
  },
  {
    key: 'TAI_XE', name: 'Tài xế', goal: 'An toàn tuyệt đối - Giao hàng đúng hẹn - Tiết kiệm chi phí',
    criteria: [
      { name: 'Hoàn thành chuyến', weight: 20 }, { name: 'An toàn giao thông', weight: 25 }, { name: 'Giao hàng đúng/đủ', weight: 15 },
      { name: 'Bảo quản hàng hóa', weight: 10 }, { name: 'Tình trạng xe', weight: 10 }, { name: 'Tiết kiệm nhiên liệu', weight: 5 },
      { name: 'Hồ sơ, chứng từ', weight: 5 }, { name: 'Thái độ phục vụ', weight: 5 }, ON_TIME, { name: 'Tuân thủ nội quy', weight: 5, autoSource: 'violations' },
    ],
  },
  {
    key: 'KHO', name: 'Kho', goal: 'Tồn kho chính xác - Xuất hàng nhanh - Kho sạch, an toàn',
    criteria: [
      { name: 'Độ chính xác tồn kho', weight: 25 }, { name: 'Xuất/nhập đúng chứng từ', weight: 15 }, { name: 'Tốc độ xử lý đơn', weight: 15 },
      { name: 'Tỷ lệ giao đúng hàng', weight: 10 }, { name: 'Hao hụt/mất mát', weight: 10 }, { name: 'Sắp xếp & 5S kho', weight: 10 },
      { name: 'Kiểm kê đúng hạn', weight: 5 }, { name: 'An toàn kho', weight: 5 }, ON_TIME, { name: 'Phối hợp nội bộ', weight: 5 },
    ],
  },
  {
    key: 'XUONG_SAN_XUAT', name: 'Xưởng sản xuất', goal: 'Đúng tiến độ - Chất lượng ổn định - An toàn tuyệt đối',
    criteria: [
      { name: 'Sản lượng đạt kế hoạch', weight: 20 }, { name: 'Tỷ lệ đạt chất lượng', weight: 20 }, { name: 'Hoàn thành đúng tiến độ', weight: 15 },
      { name: 'Tỷ lệ hàng lỗi/rework', weight: 10 }, { name: 'Hao hụt vật tư', weight: 10 }, { name: 'Hiệu suất máy/thiết bị', weight: 5 },
      { name: '5S & vệ sinh khu vực', weight: 5 }, { name: 'An toàn lao động', weight: 10 }, ON_TIME, { name: 'Kỷ luật & phối hợp', weight: 5, autoSource: 'violations' },
    ],
  },
  {
    key: 'DOI_THI_CONG', name: 'Đội thi công', goal: 'Công trình đạt chất lượng - Đúng tiến độ - An toàn - Uy tín',
    criteria: [
      { name: 'Hoàn thành tiến độ', weight: 20 }, { name: 'Chất lượng nghiệm thu', weight: 20 }, { name: 'An toàn lao động', weight: 20 },
      { name: 'Sử dụng vật tư đúng định mức', weight: 10 }, { name: 'Năng suất nhân công', weight: 10 }, { name: '5S & quản lý công trường', weight: 5 },
      { name: 'Hồ sơ/nghiệm thu', weight: 5 }, { name: 'Phối hợp với CĐT', weight: 5 }, ON_TIME, { name: 'Bảo quản máy móc, dụng cụ', weight: 5 },
    ],
  },
  {
    key: 'CHUNG', name: 'Chung (mọi phòng)', goal: 'Chuyên cần - Tuân thủ - Học tập',
    criteria: [
      { name: 'Hoàn thành công việc được giao', weight: 50 }, { name: 'Chất lượng công việc', weight: 20 }, ON_TIME,
      { name: 'Tuân thủ nội quy, chấm công', weight: 10, autoSource: 'violations' }, { name: 'Hoàn thành khóa đào tạo', weight: 10, autoSource: 'training' },
      { name: 'Thái độ & phối hợp', weight: 5 },
    ],
  },
];
